/** @jsxImportSource @emotion/react */
import NovaventaButton from "@/components/formControls/button/NovaventaButton";
import { NovaventaInput } from "@/components/formControls/input/NovaventaInput";
import NovaventaSelect from "@/components/formControls/select/NovaventaSelect";
import ModalMessage from "@/components/ModalMessage";
import TermsAndConditions from "@/components/termAndConditions/TermsAndConditions";
import { usePrismic } from "@/hooks/usePrismic";
import { getUserInfo } from "@/services/faceToFaceRegistraionService";
import { fetchPrismicDataTermsAndConditions } from "@/services/prismicService";
import {
  getDocumentType,
  sendUserRegistration
} from "@/services/selfRegistrationServices";
import { errorDictionary } from "@/utils/hooks/HandleHttpErrors";
import { removeObject, setObject } from "@/utils/hooks/localStorage";
import { css } from "@emotion/react";
import { Col, Form, Row } from "antd";
import type { RichTextBlock } from "prismic-reactjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { validateDocumentNumber } from "../validations/formValidations";

type Props = {
  center?: string;
  isServant?: boolean;
  quitLink?: JSX.Element;
  navigationPath: string;
  isProspectCustomer?: boolean;
  registrationType?: string;
};

const IdForm = (props: Props) => {
  const {
    isServant,
    quitLink,
    navigationPath,
    isProspectCustomer,
    registrationType
  } = props;

  const t = usePrismic();

  const navigate = useNavigate();
  const [termAnConditionsText, setTermAndConditionsText] = useState<RichTextBlock[]>();
  const [termAnConditionsVersion, setTermAndConditionsVersion] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalKindText, setModalKindText] = useState("");
  const [modalBtnText, setModalBtnText] = useState("");
  const [modalAlertText, setModalAlertText] = useState("");
  const [modalTextModal, setModalTextModal] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [userInfo, setUserInfo] = useState({});
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    getDocumentType(controller).then(setDocumentTypeOptions);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    removeObject("autocomplete");
    fetchPrismicDataTermsAndConditions().then((response: any) => {
      setTermAndConditionsVersion(response.version);
      if (registrationType === "phoneReg") {
        setTermAndConditionsText(response["texto-canal-telefonico"]);
      } else {
        setTermAndConditionsText(response.texto);
      }
    });
  }, []);

  const onFinish = (values: { idTipoDocumento: string; numeroDocumento: string }) => {
    const postData = {
      prospecto: {
        documento: {
          idTipoDocumento: values.idTipoDocumento,
          registro: values.numeroDocumento
        },
        canal: navigationPath.split("/")[1],
        versionTyC: termAnConditionsVersion
      }
    };
    sendUserRegistration(postData)
      .then((response: { continuar: boolean; uuid: string; codigo: string }) => {
        setObject("idType", values.idTipoDocumento);
        setObject("idNumber", values.numeroDocumento);

        if (response.continuar) return onHandleNavigate();

        if (response.codigo == "E-13") {
          removeObject("token");
          removeObject("idType");
          removeObject("idNumber");
          window.location.reload();
          return;
        }

        if (response.codigo === "E-4") {
          if (isServant) {
            setObject("autocomplete", true);
            getUserInfo(response.uuid)
              .then(({ data }) => setUserInfo(data))
              .catch(console.error);
          } else return onHandleNavigate();
        }
        setModalTextModal(errorDictionary(`MSG${response.codigo}`, t.errors));
        setModalAlertText(errorDictionary(`ART${response.codigo}`, t.errors));
        setModalBtnText(errorDictionary(`BTN${response.codigo}`, t.errors));
        setModalKindText(errorDictionary(`KND${response.codigo}`, t.errors));
        setErrorCode(response.codigo);
        setShowModal(true);
      })
      .catch(console.error);
  };

  const onHandleNavigate = () => {
    if (errorCode === "E-3") {
      const linkCommerce = String(process.env.REACT_APP_URL_COMMERCE);
      window.location.assign(linkCommerce);
    } else navigate(navigationPath, { state: { userInfo } });
  };

  const spanInputs = {
    xs: { span: 22 },
    sm: { span: 13 },
    md: { span: 10 },
    lg: { span: 8 },
    xl: { span: 6 }
  };

  return (
    <div
      css={css`
        padding-top: 2em;
      `}
    >
      <Form
        data-testid="idForm"
        autoComplete="off"
        name="idForm"
        layout="vertical"
        initialValues={{ remember: true }}
        onFinish={onFinish}
      >
        <Row justify="center">
          <Col {...spanInputs}>
            <Form.Item
              name="idTipoDocumento"
              label={
                isProspectCustomer
                  ? t.constants.TIPODOCUMENTOPROSPECTO
                  : t.constants.TIPODOCUMENTO
              }
              rules={[{ required: true, message: t.home.rules.required }]}
            >
              <NovaventaSelect
                options={documentTypeOptions}
                placeholder={t.constants.TIPODOCUMENTO2}
                optionsProp={{ label: "nombre", id: "id" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col {...spanInputs}>
            <Form.Item
              required
              name="numeroDocumento"
              label={
                isProspectCustomer
                  ? t.constants.NUMERODOCUMENTOPROSPECTO
                  : t.constants.NUMERODOCUMENTO
              }
              rules={[
                {
                  validator: (_rule: unknown, value: string) => {
                    return validateDocumentNumber(value, t.home.rules);
                  }
                }
              ]}
            >
              <NovaventaInput placeholder={t.constants.NUMERODOCUMENTO2} />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col {...spanInputs}>
            <Form.Item>
              <NovaventaButton showArrow={true} htmlType="submit" />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col {...spanInputs}>{quitLink}</Col>
        </Row>

        <TermsAndConditions
          hide={isServant}
          termAnConditionsText={termAnConditionsText}
        />
      </Form>
      <ModalMessage
        showModal={showModal}
        text={modalTextModal}
        alertText={modalAlertText}
        btnText={modalBtnText}
        kind={modalKindText}
        handleClickBtn={onHandleNavigate}
      />
    </div>
  );
};

export default IdForm;
